import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Keyboard, ScanLine } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import { scanOffcutOutbound } from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import { detectScanCodeKind, normalizeScannedCode } from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { formatWeight } from '../utils/formatters';
import { getStoredUser } from '../utils/session';

const AUTO_SCAN_DELAY_MS = 500;
const OUTBOUND_PURPOSE_OPTIONS = ['生产领用', '报废', '外销'] as const;

function ScanOffcutOutbound() {
  const inputRef = useRef<HTMLInputElement>(null);
  const resetTimerRef = useRef<number>(0);
  const scanTimerRef = useRef<number>(0);
  const [scanValue, setScanValue] = useState('');
  const [ocsf, setOcsf] = useState('');
  const [purpose, setPurpose] = useState<(typeof OUTBOUND_PURPOSE_OPTIONS)[number]>('生产领用');
  const [weight, setWeight] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const operator = getStoredUser();
  const operatorDisplay = operator?.full_name || operator?.username || operator?.emp_no || '员工';
  const step = !ocsf ? 'offcut' : 'details';
  const progressSteps = [
    {
      complete: Boolean(ocsf),
      current: step === 'offcut',
      icon: ScanLine,
      label: '料头',
    },
    {
      complete: Boolean(successMessage),
      current: step === 'details' && !successMessage,
      icon: CheckCircle2,
      label: '出库信息',
    },
    {
      complete: Boolean(successMessage),
      current: false,
      icon: CheckCircle2,
      label: '完成',
    },
  ];

  usePersistentFocus(
    inputRef,
    Boolean(operator) && !successMessage && step !== 'details',
    `${step}:${ocsf}`,
  );

  useEffect(
    () => () => {
      window.clearTimeout(resetTimerRef.current);
      window.clearTimeout(scanTimerRef.current);
    },
    [],
  );

  if (!operator) {
    navigateTo('/login', true);
    return null;
  }

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function resetFlow() {
    clearScanInput();
    setOcsf('');
    setPurpose('生产领用');
    setWeight('');
    setNote('');
    setSubmitting(false);
    setSuccessMessage('');
  }

  function scheduleReset() {
    window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(() => {
      resetFlow();
    }, 2000);
  }

  async function handleScan(rawCode?: string) {
    window.clearTimeout(scanTimerRef.current);
    const code = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);
    const kind = detectScanCodeKind(code);

    if (!code || submitting || successMessage) {
      return;
    }

    if (kind === 'offcut') {
      setOcsf(code);
      clearScanInput();
      notifySuccess(`料头 ${code}`);
      return;
    }

    if (kind === 'equipment') {
      notifyError('设备扫码功能尚未开放');
    } else if (kind === 'rawMaterial') {
      notifyError('v1.0 不处理原材料批次码');
    } else if (kind === 'location') {
      notifyError('这里要扫料头 QR');
    } else {
      notifyError('请扫描料头 QR');
    }
    clearScanInput();
  }

  function scheduleScan(rawCode: string) {
    if (submitting || successMessage || step === 'details') {
      return;
    }

    setScanValue(rawCode);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      void handleScan(inputRef.current?.value ?? rawCode);
    }, AUTO_SCAN_DELAY_MS);
  }

  async function handleSubmit() {
    if (submitting || successMessage) {
      return;
    }

    const trimmedWeight = weight.trim();
    const parsedWeight = trimmedWeight ? Number(trimmedWeight) : undefined;

    if (
      trimmedWeight
      && (
        parsedWeight == null
        || !Number.isFinite(parsedWeight)
        || parsedWeight <= 0
        || parsedWeight > 1000
      )
    ) {
      notifyError('重量要大于 0 小于 1000kg');
      return;
    }

    setSubmitting(true);
    try {
      const response = await scanOffcutOutbound({
        note: note.trim() || undefined,
        ocsf,
        reason: purpose,
        weight: parsedWeight,
      });

      const message = `已出库 ${formatWeight(response.data.weight_out)}kg，剩余 ${formatWeight(response.data.remaining_weight)}kg`;
      setSuccessMessage(message);
      notifySuccess(message);
      scheduleReset();
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '出库失败'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PdaPage
      actionLabel="重置"
      onAction={resetFlow}
      onBack={() => navigateTo('/home')}
      pageClassName="pre-inbound-page"
      subtitle="料头码 → 用途 + 可选重量"
      title="料头出库"
    >
      <section className="pre-progress-card">
        <div className="pre-progress-title">当前进度</div>
        <div className="pre-step-track">
          {progressSteps.map((item) => {
            const StepIcon = item.icon;
            const className = [
              'pre-step',
              item.complete ? 'pre-step--complete' : '',
              item.current ? 'pre-step--current' : '',
              item.complete ? 'pre-step--line-complete' : '',
            ].filter(Boolean).join(' ');

            return (
              <div className={className} key={item.label}>
                <div className="pre-step__line" />
                <div className="pre-step__icon">
                  <StepIcon aria-hidden="true" size={15} strokeWidth={2.4} />
                </div>
                <div className="pre-step__label">{item.label}</div>
              </div>
            );
          })}
        </div>
        <div className="pre-progress-count">
          <div className="pre-progress-count__label">方式</div>
          <div className="pre-progress-count__value">{weight.trim() ? '部分' : '全量'}</div>
          <div className="pre-progress-count__sub">{weight.trim() ? `${weight.trim()}kg` : '--'}</div>
        </div>
      </section>

      <section className="pre-info-card">
        <div className="pre-info-grid">
          <div className="pre-info-cell">
            <div className="pre-label">操作人</div>
            <div className="pre-value">
              {operatorDisplay}{operator.emp_no ? ` · ${operator.emp_no}` : ''}
            </div>
          </div>
          <div className="pre-info-cell">
            <div className="pre-label">料头码</div>
            <div className="pre-value">{ocsf || '等待扫码'}</div>
          </div>
        </div>
        <button className="pre-order-card" disabled={!ocsf} type="button">
          <div>
            <div className="pre-label">出库用途</div>
            <div className="pre-order-title">{purpose}</div>
            <div className="pre-order-sub">{weight.trim() ? `本次 ${weight.trim()}kg` : '不填按全量出库'}</div>
          </div>
          <span className="pre-chevron">›</span>
        </button>
      </section>

      {successMessage ? (
        <section className="panel success-panel">
          <div className="success-icon">✓</div>
          <div className="success-text">{successMessage}</div>
          <div className="panel-hint">2 秒后自动回到“等扫料头”。</div>
        </section>
      ) : step !== 'details' ? (
        <section className="pre-scan-card" key={`scan-step-${step}-${ocsf}`}>
          <div className="panel-title">先扫料头 QR</div>
          <div className="pre-scan-box">
            <ScanLine className="pre-scan-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
            <input
              autoComplete="off"
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              className="pre-scan-input"
              id="scan-outbound-code"
              inputMode="none"
              onInput={(event) => scheduleScan(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleScan(event.currentTarget.value);
                }
              }}
              placeholder="扫描后按回车..."
              ref={inputRef}
              spellCheck={false}
            />
            <Keyboard className="pre-keyboard-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
          </div>
        </section>
      ) : (
        <section className="pre-entry-card" key={`details-step-${ocsf || 'empty'}`}>
          <div className="pre-entry-head">
            <div>
              <div className="pre-entry-title">填写出库信息</div>
              <div className="pre-entry-sub">{ocsf} · {weight.trim() ? `本次 ${weight.trim()}kg` : '全量出库'}</div>
            </div>
            <span className="pre-chevron">›</span>
          </div>

          <div className="pre-field-grid">
            <label className="pre-field-cell" htmlFor="scan-outbound-purpose">
              <span>用途</span>
              <select
                id="scan-outbound-purpose"
                onChange={(event) => setPurpose(event.target.value as (typeof OUTBOUND_PURPOSE_OPTIONS)[number])}
                value={purpose}
              >
                {OUTBOUND_PURPOSE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label className="pre-field-cell" htmlFor="scan-outbound-weight">
              <span>出库重量(kg)</span>
              <input
                id="scan-outbound-weight"
                inputMode="decimal"
                onChange={(event) => setWeight(event.target.value)}
                placeholder="全量"
                value={weight}
              />
            </label>
          </div>

          <label className="pre-note-field" htmlFor="scan-outbound-reason">
            <span>备注(可选)</span>
            <input
              id="scan-outbound-reason"
              onChange={(event) => setNote(event.target.value)}
              placeholder="例如: MO-SFA1-260420-001"
              value={note}
            />
            <span className="pre-note-edit" aria-hidden="true" />
          </label>

          <div className="button-row" style={{ padding: 9 }}>
            <button className="secondary-btn" onClick={resetFlow} type="button">
              重置本次
            </button>
            <button className="primary-btn" disabled={submitting} onClick={() => void handleSubmit()} type="button">
              {submitting ? '提交中...' : '提交出库'}
            </button>
          </div>
        </section>
      )}
    </PdaPage>
  );
}

export default ScanOffcutOutbound;
